import { ChangeDetectionStrategy, Component, input, output, inject } from '@angular/core';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { ZagButton } from '@zag/ui';

export interface SocialProvider {
  providerId: string;
  displayName: string;
}

/**
 * Reusable social login buttons component.
 *
 * Renders a list of social provider buttons with brand icons, styled identically
 * to the login and register pages. Used by LoginComponent, RegisterComponent,
 * and AcceptInvitationComponent.
 *
 * @example
 * ```html
 * <sc-social-login-buttons
 *   [providers]="providers"
 *   [disabled]="isLoading"
 *   (providerClick)="onSocialLogin($event)"
 * />
 * ```
 */
@Component({
  selector: 'sc-social-login-buttons',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslateModule, ZagButton],
  template: `
    <div class="social-login-section">
      @for (provider of providers(); track provider.providerId) {
        <button
          zagButton="outlined"
          zagType="secondary"
          type="button"
          class="social-login-button"
          [disabled]="disabled()"
          (click)="providerClick.emit(provider.providerId)"
        >
          @if (provider.providerId === 'microsoft') {
            <svg class="social-icon" viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <rect x="1" y="1" width="9" height="9" fill="#f25022" />
              <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
              <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
              <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
            </svg>
          }
          @if (provider.providerId === 'google') {
            <svg class="social-icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                fill="#4285F4"
              />
              <path
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                fill="#34A853"
              />
              <path
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"
                fill="#FBBC05"
              />
              <path
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                fill="#EA4335"
              />
            </svg>
          }
          @if (provider.providerId === 'apple') {
            <svg class="social-icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path
                d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"
              />
            </svg>
          }
          @if (provider.providerId === 'linkedin') {
            <svg class="social-icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path
                fill="#0A66C2"
                d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"
              />
            </svg>
          }
          {{ 'auth.continueWith' | translate: { provider: getProviderName(provider) } }}
        </button>
      }
    </div>
  `,
  styles: [
    `
      .social-login-section {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
      }

      .social-login-button {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 0.5rem;
        width: 100%;
        padding: 0.75rem 1rem;
        font-size: 0.9375rem;
        font-weight: 500;
      }

      .social-icon {
        width: 20px;
        height: 20px;
        flex-shrink: 0;
      }
    `,
  ],
})
export class SocialLoginButtonsComponent {
  private readonly translate = inject(TranslateService);

  /** List of social providers to render */
  readonly providers = input.required<SocialProvider[]>();

  /** Whether the buttons should be disabled */
  readonly disabled = input(false);

  /** Emitted with the provider ID when a button is clicked */
  readonly providerClick = output<string>();

  protected getProviderName(provider: SocialProvider): string {
    const key = `auth.socialProviders.${provider.providerId}`;
    const translated = this.translate.instant(key);
    return translated === key ? provider.displayName : translated;
  }
}
